import { useCallback, useEffect, useState } from "react";
import { pushActivity } from "../utils/activity";
import { API_BASE } from "../config/apiBase";

/**
 * Manage user recordings. Optional slug filter.
 *
 * Server expectations:
 *  - GET    /me/recordings?slug=xxx -> list
 *  - POST   /me/recordings          -> returns saved row {id, url, created_at, ...}
 *  - DELETE /me/recordings/:id
 */

/**
 * Ensure URLs always point to the API server (e.g. :5050),
 * not the React dev server (:3000).
 */
const joinUrl = (base, path) => {
    const b = String(base || "").replace(/\/$/, "");
    const p = String(path || "");

    if (!p) return p;
    if (p.startsWith("http://") || p.startsWith("https://")) return p;
    if (p.startsWith("/")) return `${b}${p}`;
    return `${b}/${p}`;
};

export default function useRecordings({
    token,
    apiBase = API_BASE,
    questionSlug, // optional
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(!!token);
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);

    /* ----------------------------- LOAD ----------------------------- */
    const loadRecordings = useCallback(
        async ({ signal, silent = false } = {}) => {
            if (!token) return;

            if (!silent) setLoading(true);
            setError(null);

            const qs = questionSlug
                ? `?slug=${encodeURIComponent(questionSlug)}`
                : "";

            try {
                const res = await fetch(`${apiBase}/me/recordings${qs}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    signal,
                });

                if (!res.ok) {
                    throw new Error(`Load recordings failed: ${res.status}`);
                }

                const rows = await res.json();

                // ✅ Normalise URLs + names
                const normalised = rows.map((r) => ({
                    ...r,
                    url: joinUrl(apiBase, r.url),
                    name: r.filename || r.name || "Recording",
                }));

                setItems(normalised);
            } catch (e) {
                if (e.name !== "AbortError") {
                    setError(e);
                }
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [token, questionSlug, apiBase]
    );

    useEffect(() => {
        if (!token) return;

        const ac = new AbortController();
        loadRecordings({ signal: ac.signal });

        return () => ac.abort();
    }, [token, questionSlug, apiBase, loadRecordings]);

    useEffect(() => {
        if (!token || !items.length) return;
        const hasPending = items.some(
            (rec) =>
                rec.transcript_status === "pending" ||
                rec.feedback_status === "pending"
        );
        if (!hasPending) return;

        const ac = new AbortController();
        const id = setTimeout(() => {
            loadRecordings({ signal: ac.signal, silent: true });
        }, 4000);

        return () => {
            clearTimeout(id);
            ac.abort();
        };
    }, [token, items, loadRecordings]);

    /* ---------------------------- UPLOAD ----------------------------- */
    const upload = useCallback(
        async ({
            blob,
            filename,
            question_slug,
            duration_ms,
            evaluation_style,
            evaluation_criteria,
        }) => {
            if (!token) throw new Error("No token");

            setUploading(true);

            try {
                const fd = new FormData();
                fd.append(
                    "file",
                    blob,
                    filename || `recording-${Date.now()}.webm`
                );
                fd.append("question_slug", question_slug);

                if (duration_ms) {
                    fd.append("duration_ms", String(duration_ms));
                }
                if (evaluation_style) {
                    fd.append("evaluation_style", String(evaluation_style));
                }
                if (evaluation_criteria) {
                    fd.append("evaluation_criteria", String(evaluation_criteria));
                }

                const res = await fetch(`${apiBase}/me/recordings`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: fd,
                });

                if (!res.ok) {
                    throw new Error(`Upload failed: ${res.status}`);
                }

                const row = await res.json();

                // ✅ Normalise POST response as well
                const normalisedRow = {
                    ...row,
                    url: joinUrl(apiBase, row.url),
                    name: row.filename || row.name || "Recording",
                };

                setItems((prev) => [normalisedRow, ...prev]);
                pushActivity({
                    type: "recording",
                    question_slug: question_slug,
                    recording_id: normalisedRow.id,
                    url: normalisedRow.url,
                    ts: normalisedRow.created_at
                        ? new Date(normalisedRow.created_at).getTime()
                        : Date.now(),
                });
                return normalisedRow;
            } finally {
                setUploading(false);
            }
        },
        [token, apiBase]
    );

    /* ---------------------------- DELETE ----------------------------- */
    const remove = useCallback(
        async (id) => {
            if (!token || !id) return;

            const prev = items;
            setItems((cur) => cur.filter((r) => r.id !== id)); // optimistic UI

            try {
                const res = await fetch(`${apiBase}/me/recordings/${id}`, {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!res.ok) {
                    throw new Error(`Delete failed: ${res.status}`);
                }
            } catch (e) {
                setItems(prev); // rollback
                throw e;
            }
        },
        [token, apiBase, items]
    );

    return {
        items,
        loading,
        error,
        uploading,
        upload,
        remove,
        setItems,
    };
}
